import { get_token } from "@/router";

const bolide_assist_query_url = process.env.VUE_APP_ASSIST_QUERY_URL;
const query_poll_interval_ms = 1500;
const query_poll_timeout_ms = 180000;
const query_id_pattern = /^[A-Za-z0-9_-]+$/;

function copy_present_property(target, source, property) {
  if (source[String(property)] != null && source[String(property)] !== "") {
    target[String(property)] = source[String(property)];
  }
}

export default {
  async query_assist(query_text, progress_context = {}) {
    const response = await fetch(bolide_assist_query_url, {
      method: "POST",
      headers: this.get_assist_request_headers(),
      body: JSON.stringify(
        this.build_assist_request_body(query_text, progress_context.conversation_id)
      ),
    });

    const data = await this.read_assist_response_json(response);
    this.assert_assist_response_ok(response, data);
    this.update_query_progress(progress_context, data);
    return this.is_assist_query_pending(data)
      ? this.poll_assist_query(this.get_assist_query_id(data), progress_context)
      : this.get_assist_response_text(data);
  },

  async poll_assist_query(query_id, progress_context = {}) {
    const detail_request = this.build_assist_query_detail_request(query_id);
    const started_at = Date.now();

    while (Date.now() - started_at < query_poll_timeout_ms) {
      const result = await this.poll_assist_query_once(detail_request, progress_context);
      if (result.complete) return this.get_assist_response_text(result.data);
    }
    throw new Error(
      "AI Campus VSI is still working on that request. Please check the chat history shortly."
    );
  },

  async poll_assist_query_once(detail_request, progress_context) {
    await this.wait(query_poll_interval_ms);
    const response = await this.send_assist_detail_request(detail_request);
    const data = await this.read_assist_response_json(response);
    this.assert_assist_response_ok(response, data);
    this.update_query_progress(progress_context, data);

    if (data.status === "FAILED") {
      throw new Error(this.get_assist_failure_message(data));
    }
    return ({
      complete: data.status === "COMPLETED",
      data,
    });
  },

  send_assist_detail_request(detail_request) {
    return new Promise((resolve, reject) => {
      const request = new XMLHttpRequest();
      request.open("GET", detail_request.url, true);
      detail_request.headers.forEach((value, name) => {
        request.setRequestHeader(name, value);
      });
      request.onload = () => resolve({
        ok: request.status >= 200 && request.status < 300,
        status: request.status,
        json: async () => JSON.parse(request.responseText),
      });
      request.onerror = () => reject(new Error("AI Campus VSI polling request failed."));
      request.send();
    });
  },

  build_assist_request_body(query_text, chat_id = null) {
    const body = {
      query_text,
      chat_id: chat_id || this.active_conversation_id || "default",
    };
    const scope = this.build_request_scope();
    if (scope) body.scope = scope;
    return body;
  },

  build_request_scope() {
    if (!this.scope?.level) return null;

    const scope = { level: this.scope.level };
    ["property_id", "property_name", "domain_hint"].forEach((property) => {
      copy_present_property(scope, this.scope, property);
    });
    if (this.scope.compare_property_ids?.length) {
      scope.compare_property_ids = this.scope.compare_property_ids;
    }
    return scope;
  },

  async fetch_assist_history(params = {}) {
    const response = await fetch(this.build_assist_history_url(params), {
      method: "GET",
      headers: this.get_assist_request_headers(),
    });
    const data = await this.read_assist_response_json(response);
    this.assert_assist_response_ok(response, data);
    return this.normalize_assist_page(data);
  },

  async fetch_assist_chat_summaries(params = {}) {
    const response = await fetch(this.build_assist_chat_history_url(params), {
      method: "GET",
      headers: this.get_assist_request_headers(),
    });
    const data = await this.read_assist_response_json(response);
    this.assert_assist_response_ok(response, data);
    return this.normalize_assist_page(data);
  },

  build_assist_history_url(params = {}) {
    return this.build_url_with_params(this.build_assist_history_base_url(), params);
  },

  build_assist_chat_history_url(params = {}) {
    return this.build_url_with_params(
      new URL("chats/", this.build_assist_history_base_url()).toString(),
      params
    );
  },

  build_assist_history_base_url() {
    if (!bolide_assist_query_url) {
      throw new Error("VUE_APP_ASSIST_QUERY_URL is not configured.");
    }

    const query_url = String(bolide_assist_query_url).replace(/\/+$/, "");
    return new URL(
      `${query_url.replace(/\/query$/, "/history")}/`,
      window.location.origin
    ).toString();
  },

  build_url_with_params(url_value, params = {}) {
    const history_url = new URL(url_value, window.location.origin);
    Object.entries(params).forEach(([key, value]) => {
      if (value != null && value !== "") history_url.searchParams.set(key, value);
    });
    return history_url.toString();
  },

  normalize_assist_page(data) {
    return ({
      ...data,
      count: Number(data.count || 0),
      next_cursor: data.next_cursor || null,
      results: Array.isArray(data.results) ? data.results : [],
    });
  },

  get_assist_request_headers() {
    if (!bolide_assist_query_url) {
      throw new Error("VUE_APP_ASSIST_QUERY_URL is not configured.");
    }
    const auth_token = get_token();
    if (!auth_token) throw new Error("No authentication token found.");
    return ({
      Authorization: `Token ${auth_token}`,
      "Content-Type": "application/json",
      Accept: "application/json",
    });
  },

  build_assist_query_detail_url(query_id) {
    const safe_query_id = String(query_id || "");
    if (!query_id_pattern.test(safe_query_id)) {
      throw new Error("AI Campus VSI returned an invalid query id.");
    }
    const encoded_query_id = encodeURIComponent(safe_query_id);
    return `${String(bolide_assist_query_url).replace(/\/+$/, "")}/${encoded_query_id}/`;
  },

  build_assist_query_detail_request(query_id) {
    const base_url = window.location.origin;
    const detail_url = new URL(this.build_assist_query_detail_url(query_id), base_url);
    const configured_url = new URL(bolide_assist_query_url, base_url);
    if (detail_url.origin !== configured_url.origin) {
      throw new Error("AI Campus VSI returned an invalid query URL.");
    }
    return new Request(detail_url.toString(), {
      method: "GET",
      headers: this.get_assist_request_headers(),
    });
  },

  async read_assist_response_json(response) {
    return response.json().catch(() => ({}));
  },

  assert_assist_response_ok(response, data) {
    if (response.ok) return;
    const error = new Error(this.get_assist_error_message(response, data));
    error.status = response.status;
    error.data = data;
    throw error;
  },

  get_assist_error_message(response, data) {
    return data.detail
    || data.query?.response_text
    || data.error
    || `AI Campus VSI request failed: ${response.status}`;
  },

  get_optional_assist_response_text(data) {
    return data.response_text || data.query?.response_text || "";
  },

  get_assist_response_text(data) {
    const response_text = this.get_optional_assist_response_text(data);
    if (response_text) return response_text;
    throw new Error("AI Campus VSI returned an empty response.");
  },

  get_assist_failure_message(data) {
    return this.get_optional_assist_response_text(data)
      || "AI Campus VSI could not process that request.";
  },

  is_assist_query_pending(data) {
    const query = data.query || data;
    return ["QUEUED", "PROCESSING"].includes(query?.status);
  },

  get_assist_query_id(data) {
    return data.id || data.query?.id || null;
  },

  update_query_progress(progress_context, data) {
    if (!progress_context.conversation_id || !progress_context.message_id) return;
    this.update_thinking_steps(
      progress_context.conversation_id,
      progress_context.message_id,
      data
    );
  },
};
